import {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle
} from 'react';

import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Yup from '../../../utils/yupExtensions';
import styles from './EditJob.module.css';
import JobSummary from './JobSummary';
import JobOverView from './JobOverView';
import GlobalModal from '../../../components/UI/Popups/GlobalModal';
import BubbleButton from '../../../components/UI/Buttons/BubbleButton/BubbleButton';
import QualificationAndSkills from './QualificationAndSkills';
import AdditionalRequirements from './AdditionalRequirements';
import CompensationAndbenefits from './CompensationAndbenefits';
import { useGetJobCategoriesHook } from '../../../utils/utilityHooks';
import { getJobByID, updateJob, getJobsList } from '../../../services/jobService';

const EditJob = forwardRef(({ activeSteps, setActiveSteps, setPostJobEnable }, ref) => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [formLoading, setFormLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    const [draftJobId, setDraftJobId] = useState(id || null);
    const [validationErrors, setValidationErrors] = useState({});
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [submittedJobData, setSubmittedJobData] = useState({ title: '', id: null, isActive: false });

    const userToken = useSelector(state => state.user.token);
    const user = useSelector(state => state.user.user);
    const features = useSelector(state => state.user.features);
    const categoryOptions = useGetJobCategoriesHook();

    const defaultValue = {
        specifiedForCountry: false,
        specifiedForCity: false,
        title: '',
        endDate: null,
        minworkexp: '',
        maxworkexp: '',
        description: '',
        country: '',
        category: '',
        education_level: '',
        skills: [],
        role_skills: [],
        city: '',
        languages: [],
        exlusiveJob: false,
        job_type: 'full_time',
        job_location: 'remote',
        hours_low: 30,
        hours_high: 40,
        salary_low: 300,
        salary_high: 500,
        currency: 'usd',
        nationality: '',
        visaRequirment: 'Yes',
        salaryRate: 'per_hour',
        otherBenefit: [],
        licenseRequire: 'Yes',
        nationalityRequirment: 'Yes',
        working_hours_per_week_from: 10,
        working_hours_per_week_to: 40,
        status: 'draft'
    };

    const initialValues = defaultValue

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object({
            title: Yup.string().required(t('general.UI.required')),
            country: Yup.string().required(t('general.UI.required')),
            category: Yup.string().required(t('general.UI.required')),
            city: Yup.string().required(t('general.UI.required')),
            // payment_type: Yup.string().required(t('general.UI.required')),
            exlusiveJob: Yup.string().required(t('general.UI.required')),
            education_level: Yup.string().required(t('general.UI.required')),
            job_type: Yup.string().required(t('general.UI.required')),
            job_location: Yup.string().required(t('general.UI.required')),
            hours_low: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).max(168, t('general.UI.maximum_possible_value_is_168')).required(t('general.UI.required')),
            hours_high: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).max(168, t('general.UI.maximum_possible_value_is_168')).required(t('general.UI.required')),
            salary_low: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            salary_high: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            endDate: Yup.string().required(t('forms.education.required')),
            working_hours_per_week_to: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            working_hours_per_week_from: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            minworkexp: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            maxworkexp: Yup.number().min(1, t('forms.manage_job.minimum_possible_value_is_1')).required(t('general.UI.required')),
            nationality: Yup.string().when('nationalityRequirment', {
                is: 'Yes',
                then: (schema) => schema.required(t('general.UI.required')),
                otherwise: (schema) => schema.notRequired(),
            }),
            languages: Yup.array()
                .of(Yup.string().required(t('general.UI.required')))
                .min(1, t('general.UI.required')),
            role_skills: Yup.array()
                .of(Yup.string().required(t('general.UI.required')))
                .min(1, t('general.UI.required')),
        }),
        onSubmit: async (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);
            setSubmitLoading(true);
            const formData = new FormData();
            formData.append('token', userToken);

            // Basic required fields that shouldn't be empty
            formData.append('title', values.title || '');
            formData.append('city', values.city || '');
            formData.append('country', values.country || '');
            formData.append('category', values.category || '');
            
            // Use the HTML version of description if available, otherwise use the plain text or original description
            const descriptionToSubmit = values.descriptionHtml || values.descriptionPlainText || values.description;
            formData.append('description', typeof descriptionToSubmit === 'string' ? descriptionToSubmit : JSON.stringify(descriptionToSubmit));

            // Boolean fields
            formData.append('specifiedforcountry', values.specifiedForCountry ? true : false);
            formData.append('specifiedforcity', values.specifiedForCity ? true : false);

            // Numeric fields - ensure they're always valid numbers
            formData.append('minworkexp', values.minworkexp ? Number(values.minworkexp) : 0);
            formData.append('maxworkexp', values.maxworkexp ? Number(values.maxworkexp) : 0);

            // String fields
            formData.append('nationalityrequirment', values.nationalityRequirment || 'Yes');
            formData.append('licenserequire', values.licenseRequire || 'Yes');
            formData.append('visarequirment', values.visaRequirment || 'Yes');
            formData.append('salaryrate', values.salaryRate || 'per_hour');
            formData.append('otherbenefit', values.otherBenefit || []);
            formData.append('enddate', values.endDate || new Date().toISOString());
            formData.append(`education_level`, values.education_level || '');

            // More fields
            formData.append('exlusiveJob', values.exlusiveJob === true ? 'true' : 'false');
            formData.append('job_type', values.job_type || 'full_time');
            formData.append('job_location', values.job_location || 'remote');
            formData.append('currency', values.currency || 'usd');

            // Numeric fields with currency conversions
            const salaryLow = values.salary_low ? Number(values.salary_low) : 0;
            const salaryHigh = values.salary_high ? Number(values.salary_high) : 0;

            formData.append('salary_from_aed', salaryLow * 3.67);
            formData.append('salary_from_sar', salaryLow * 3.75);
            formData.append('salary_from_qar', salaryLow * 3.66);
            formData.append('salary_from_omr', salaryLow * 0.38);
            formData.append('salary_from_kwd', salaryLow * 0.31);
            formData.append('salary_from_bhd', salaryLow * 0.38);
            formData.append('salary_from', salaryLow);
            formData.append('salary_to', salaryHigh);
            formData.append('salary_to_aed', salaryHigh * 3.67);
            formData.append('salary_to_sar', salaryHigh * 3.75);
            formData.append('salary_to_qar', salaryHigh * 3.66);
            formData.append('salary_to_omr', salaryHigh * 0.38);
            formData.append('salary_to_kwd', salaryHigh * 0.31);
            formData.append('salary_to_bhd', salaryHigh * 0.38);

            // Hours fields
            const hoursLow = values.hours_low ? Number(values.hours_low) : 0;
            const hoursHigh = values.hours_high ? Number(values.hours_high) : 0;
            formData.append('hours_from', hoursLow);
            formData.append('hours_to', hoursHigh);

            formData.append('job_status', user.employer_status || 'active');
            
            // Handle job status based on context:
            // 1. If there's an explicit status set (e.g., from the submit button), use it
            // 2. For draft jobs (draftJobId exists), set to active/pending when submitting
            // 3. For new jobs, set to active/pending based on feature flag
            // 4. For existing published jobs (id exists but not draftJobId), don't override status
             if (values.status) {
                formData.append('status', features.instantJobActivation ? 'active' : 'pending');
                // formData.append('status', values.status);
            } else if (draftJobId || !id) {
                formData.append('status', features.instantJobActivation ? 'active' : 'pending');
            }
            
            formData.append('nationality', values.nationality || '');

            if (values.languages && values.languages.length > 0) {
                values.languages.forEach((language, index) => {
                    if (language) {
                        formData.append(`languages[${index}][languageCode]`, language);
                    }
                });
            }
            else {
                formData.append('languages', '');
            }

            if (values.skills && values.skills.length > 0) {
                values.skills.forEach((skill, index) => {
                    if (skill) {
                        formData.append(`skills[${index}][code]`, skill);
                    }
                });
            }
            else {
                formData.append('skills', '');
            }

            if (values.role_skills && values.role_skills.length > 0) {
                values.role_skills.forEach((skill, index) => {
                    if (skill) {
                        formData.append(`role_skills[${index}][code]`, skill);
                    }
                });
            }
            else {
                formData.append('role_skills', '');
            }

            if (draftJobId) {
                formData.append('job_id', draftJobId);
            }
            else if (id) {
                formData.append('job_id', id);
            }

            // Dismiss all existing toasts before showing a new one
            toast.dismiss();

            toast.promise(updateJob(formData), {
                loading: t('forms.login.processing') || 'Processing...',
                success: <b>{t('job.job_published') || 'Job published successfully!'}</b>,
                error: (err) => {
                     return <b>{err.error || t('forms.login.error') || 'Error occurred'}</b>;
                },
            })
                .then((response) => {
                    // Store job data for the success modal
                    setSubmittedJobData({
                        title: values.title || t('job.your_job'),
                        id: response?.job_slug,
                        isActive: features.instantJobActivation
                    });
                    
                    setShowSuccessModal(true);
                    setSubmitLoading(false);
                 })
                .catch((error) => {
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                    setFormLoading(false);
                    setSubmitLoading(false);
                });
        },
    });

    // Validation function for step navigation
    const validateStep = (currentStep, targetStep) => {
        // If going backwards, always allow
        if (targetStep < currentStep) {
            return true;
        }

        let isValid = true;
        const errors = {};

        // If we're going forward, validate the current step
        if (targetStep > currentStep) {
            // Step 1 validation (Job Overview)
            if (currentStep === 1) {
                if (!formik.values.title) {
                    errors.title = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.country) {
                    errors.country = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.category) {
                    errors.category = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.city) {
                    errors.city = t('general.UI.required');
                    isValid = false;
                }
                // Description optional for moving to step 2
            }
            // Step 2 validation (Qualification And Skills)
            else if (currentStep === 2) {
                if (!formik.values.education_level) {
                    errors.education_level = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.skills || formik.values.skills.length === 0) {
                    errors.skills = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.role_skills || formik.values.role_skills.length === 0) {
                    errors.role_skills = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.minworkexp) {
                    errors.minworkexp = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.maxworkexp) {
                    errors.maxworkexp = t('general.UI.required');
                    isValid = false;
                }
            }
            // Step 3 validation (Additional Requirements)
            else if (currentStep === 3) {
                if (formik.values.nationalityRequirment === 'Yes' && !formik.values.nationality) {
                    errors.nationality = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.languages || formik.values.languages.length === 0) {
                    errors.languages = t('general.UI.required');
                    isValid = false;
                }
            }
            // Step 4 validation (Compensation And Benefits)
            else if (currentStep === 4) {
                if (!formik.values.job_type) {
                    errors.job_type = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.job_location) {
                    errors.job_location = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.hours_low) {
                    errors.hours_low = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.hours_high) {
                    errors.hours_high = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.salary_low) {
                    errors.salary_low = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.salary_high) {
                    errors.salary_high = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.currency) {
                    errors.currency = t('general.UI.required');
                    isValid = false;
                }
                if (!formik.values.endDate) {
                    errors.endDate = t('general.UI.required');
                    isValid = false;
                }
            }
        }

        // Update validation errors state
        if (!isValid) {
            setValidationErrors(errors);
            formik.setErrors(errors);
            // Dismiss all existing toasts before showing a new one
            toast.dismiss();
            toast.error(t('job.please_fill_required_fields'));
        } else {
            setValidationErrors({});
        }

        return isValid;
    };
    // Function to save job as draft when moving to next step
    const saveAsDraft = async (nextStep) => {
        if (formLoading) return;

        setFormLoading(true);
        setSubmitLoading(true);
        const formData = new FormData();
        formData.append('token', userToken);

        // Basic required fields that shouldn't be empty
        formData.append('title', formik.values.title || '');
        formData.append('city', formik.values.city || '');
        formData.append('country', formik.values.country || '');
        formData.append('category', formik.values.category || '');
        
        // Use the HTML version of description if available, otherwise use the plain text or original description
        const descriptionToSubmit = formik.values.descriptionHtml || formik.values.descriptionPlainText || formik.values.description;
        formData.append('description', typeof descriptionToSubmit === 'string' ? descriptionToSubmit : JSON.stringify(descriptionToSubmit));

        // Boolean fields
        formData.append('specifiedforcountry', formik.values.specifiedForCountry ? true : false);
        formData.append('specifiedforcity', formik.values.specifiedForCity ? true : false);

        // Numeric fields - ensure they're always valid numbers
        formData.append('minworkexp', formik.values.minworkexp ? Number(formik.values.minworkexp) : 0);
        formData.append('maxworkexp', formik.values.maxworkexp ? Number(formik.values.maxworkexp) : 0);

        // String fields
        formData.append('nationalityrequirment', formik.values.nationalityRequirment || 'Yes');
        formData.append('licenserequire', formik.values.licenseRequire || 'Yes');
        formData.append('visarequirment', formik.values.visaRequirment || 'Yes');
        formData.append('salaryrate', formik.values.salaryRate || 'per_hour');
        formData.append('otherbenefit', formik.values.otherBenefit || []);

        const date = new Date();
        date.setDate(date.getDate() + 30);
        formik.setFieldValue('endDate', date.toISOString().split('T')[0]);
   
        formData.append(`education_level`, formik.values.education_level || '');

        // More fields
        formData.append('exlusiveJob', formik.values.exlusiveJob === true ? 'true' : 'false');
        formData.append('job_type', formik.values.job_type || 'full_time');
        formData.append('job_location', formik.values.job_location || 'remote');
        formData.append('currency', formik.values.currency || 'usd');

        // Numeric fields with currency conversions
        const salaryLow = formik.values.salary_low ? Number(formik.values.salary_low) : 0;
        const salaryHigh = formik.values.salary_high ? Number(formik.values.salary_high) : 0;

        formData.append('salary_from_aed', salaryLow * 3.67);
        formData.append('salary_from_sar', salaryLow * 3.75);
        formData.append('salary_from_qar', salaryLow * 3.66);
        formData.append('salary_from_omr', salaryLow * 0.38);
        formData.append('salary_from_kwd', salaryLow * 0.31);
        formData.append('salary_from_bhd', salaryLow * 0.38);
        formData.append('salary_from', salaryLow);
        formData.append('salary_to', salaryHigh);
        formData.append('salary_to_aed', salaryHigh * 3.67);
        formData.append('salary_to_sar', salaryHigh * 3.75);
        formData.append('salary_to_qar', salaryHigh * 3.66);
        formData.append('salary_to_omr', salaryHigh * 0.38);
        formData.append('salary_to_kwd', salaryHigh * 0.31);
        formData.append('salary_to_bhd', salaryHigh * 0.38);

        // Hours fields
        const hoursLow = formik.values.hours_low ? Number(formik.values.hours_low) : 0;
        const hoursHigh = formik.values.hours_high ? Number(formik.values.hours_high) : 0;
        formData.append('hours_from', hoursLow);
        formData.append('hours_to', hoursHigh);

        formData.append('job_status', user.employer_status || 'active');
        
        // Only set status to 'draft' for new jobs, preserve existing status for published jobs
        if (id) {
            // For existing jobs (editing mode), don't change the status during step navigation
            // Don't set any status so the backend will preserve the existing one
            // Don't include status in formData at all during step navigation
        } else {
            // For new jobs, set status to 'draft'
            formData.append('status', 'draft');
        }
        
        formData.append('nationality', formik.values.nationality || '');

        if (formik.values.languages && formik.values.languages.length > 0) {
            formik.values.languages.forEach((language, index) => {
                if (language) {
                    formData.append(`languages[${index}][languageCode]`, language);
                }
            });
        }
        else {
            formData.append('languages', '');
        }

        if (formik.values.skills && formik.values.skills.length > 0) {
            formik.values.skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('skills', '');
        }

        if (formik.values.role_skills && formik.values.role_skills.length > 0) {
            formik.values.role_skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`role_skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('role_skills', '');
        }

        if (draftJobId) {
            formData.append('job_id', draftJobId);
        }
        else if (id) {
            formData.append('job_id', id);
        }

        try {
            // Dismiss all existing toasts before showing a new one
            toast.dismiss();

            // Show a toast notification for saving as draft
            const updatePromise = updateJob(formData);

            toast.promise(updatePromise, {
                loading: t('forms.login.processing') || 'Processing...',
                success: <b>{id ? t('job.job_updated') || 'Job updated!' : t('job.job_saved_as_draft') || 'Job saved as draft!'}</b>,
                error: (err) => <b>{err.error || t('forms.login.error') || 'Error occurred'}</b>,
            });

            const response = await updatePromise;

            // If this is a new job creation (first save), store the job ID
            if (!draftJobId && !id && response && response.job_id) {
                setDraftJobId(response.job_id);
                localStorage.setItem('draftJobId', response.job_id);
            }

            // On successful draft save, proceed to the next step
            setActiveSteps(nextStep);
            setFormLoading(false);
            setSubmitLoading(false);
        } catch (error) {
             if (error?.response?.data?.field) {
                formik.setErrors({
                    [error.response.data.field]: error.response.data.error
                });
            }
            setFormLoading(false);
            setSubmitLoading(false);
        }
    };

    // Add new function to support jumping to any step
    const jumpToStep = (targetStep) => {
        // If just going to adjacent step, use existing methods
        if (targetStep === activeSteps + 1) {
            if (validateStep(activeSteps, targetStep)) {
                saveAsDraft(targetStep);
            }
            return;
        }

        // If jumping multiple steps ahead, validate all steps in between
        // and then save the data
        if (targetStep > activeSteps + 1) {
            let allStepsValid = true;

            // Validate each step sequentially
            for (let i = activeSteps; i < targetStep; i++) {
                if (!validateStep(i, i + 1)) {
                    allStepsValid = false;
                    break;
                }
            }

            if (allStepsValid) {
                saveAsDraft(targetStep);
            }
        } else {
            // Going backwards doesn't need validation
            setActiveSteps(targetStep);
        }
    };

    // Expose both methods via ref
    useImperativeHandle(ref, () => ({
        validateStep,
        jumpToStep
    }), [formik.values, activeSteps]);

    useEffect(() => {
        if (draftJobId && draftJobId !== id) {
            // Load the draft job data if we have a draft job ID
            getJobByID(draftJobId)
                .then((response) => {
                    delete response.id;
                    response.languages = response.languages.map((language) => language.languageCode);
                    response.skills = response.skills.map((skill) => skill.skill_code);
                    response.role_skills = response.role_skills.map((skill) => skill.skill_code);
                    response.exlusiveJob = response.exlusiveJob === 'true' || response.exlusiveJob === true;
                    response.category = Number(response.category)
                    response.nationalityRequirment = response.nationalityrequirment
                    response.visaRequirment = response.visarequirment
                    response.licenseRequire = response.licenserequire
                    response.salaryRate = response.salaryrate
                    response.working_hours_per_week_from = response.hours_low
                    response.working_hours_per_week_to = response.hours_high
                    response.endDate = response.enddate
                    if (response.specifiedforcity == 'true') response.specifiedForCity = true
                    if (response.specifiedforcountry == 'true') response.specifiedForCountry = true
                    response.otherBenefit = response.otherbenefit ? response.otherbenefit.split(",") : [];
                    // Make sure to include the job status
                    response.status = response.status || 'draft';
                    formik.setValues({ ...response });
                })
                .catch((error) => {
                    if (error?.error === 'Job not found') {
                        // If the draft job is not found, clear the local storage
                        localStorage.removeItem('draftJobId');
                        setDraftJobId(null);
                    }
                 });
        } else if (id) {
            getJobByID(id)
                .then((response) => {
                    delete response.id;
                    console.log(response, '________response________');
                    response.languages = response.languages.map((language) => language.languageCode);
                    response.skills = response.skills.map((skill) => skill.skill_code);
                    response.role_skills = response.role_skills.map((skill) => skill.skill_code);
                    response.exlusiveJob = response.exlusiveJob === 'true' || response.exlusiveJob === true;
                    response.category = Number(response.category)
                    response.nationalityRequirment = response.nationalityrequirment
                    response.visaRequirment = response.visarequirment
                    response.licenseRequire = response.licenserequire
                    response.salaryRate = response.salaryrate
                    response.working_hours_per_week_from = response.hours_low
                    response.working_hours_per_week_to = response.hours_high
                    response.endDate = response.enddate
                    if (response.specifiedforcity == 'true') response.specifiedForCity = true
                    if (response.specifiedforcountry == 'true') response.specifiedForCountry = true
                    response.otherBenefit = response.otherbenefit ? response.otherbenefit.split(",") : [];
                    // Make sure to include the job status
                    response.status = response.status || 'active';
                    formik.setValues({ ...response });
                })
                .catch((error) => {
                    if (error?.error === 'Job not found') {
                        navigate('/my-jobs');
                    }
                 });
        } else {
            formik.setValues(initialValues);
        }
    }, [draftJobId, id, user.id, navigate]);

    useEffect(() => {
        if (!id && !draftJobId) {
            localStorage.setItem('formData', JSON.stringify(formik.values));
        }
    }, [formik.values, id, draftJobId]);

    useEffect(() => {
        if (!id) {
            localStorage.setItem('activeStep', activeSteps);
        }
    }, [activeSteps, id]);
 

    // Render modified form components with next button that saves as draft
    const renderFormStep = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                return (
                    <>
                        <JobOverView
                            setActiveSteps={(nextStep) => {
                                jumpToStep(nextStep);
                            }}
                            activeSteps={activeSteps}
                            formik={formik}
                            errors={validationErrors}
                            isEditMode={!!id || !!draftJobId}
                            saveAsDraftOnly={saveAsDraftOnly}
                            updateJobOnly={updateJobOnly}
                            id={id}
                            draftLoading={draftLoading}
                            submitLoading={submitLoading}
                            formik_values_status={formik.values.status}
                        />
                    </>
                );
            case 2:
                return (
                    <>
                        <QualificationAndSkills
                            setActiveSteps={(nextStep) => {
                                jumpToStep(nextStep);
                            }}
                            activeSteps={activeSteps}
                            formik={formik}
                            errors={validationErrors}
                            saveAsDraftOnly={saveAsDraftOnly}
                            updateJobOnly={updateJobOnly}
                            id={id}
                            draftLoading={draftLoading}
                            submitLoading={submitLoading}
                            formik_values_status={formik.values.status}
                        />
                    </>
                );
            case 3:
                return (
                    <>
                        <AdditionalRequirements
                            setActiveSteps={(nextStep) => {
                                jumpToStep(nextStep);
                            }}
                            activeSteps={activeSteps}
                            formik={formik}
                            errors={validationErrors}
                            saveAsDraftOnly={saveAsDraftOnly}
                            updateJobOnly={updateJobOnly}
                            id={id}
                            draftLoading={draftLoading}
                            submitLoading={submitLoading}
                            formik_values_status={formik.values.status}
                        />
                    </>
                );
            case 4:
                return (
                    <>
                        <CompensationAndbenefits
                            setActiveSteps={(nextStep) => {
                                jumpToStep(nextStep);
                            }}
                            activeSteps={activeSteps}
                            formik={formik}
                            errors={validationErrors}
                            saveAsDraftOnly={saveAsDraftOnly}
                            updateJobOnly={updateJobOnly}
                            id={id}
                            draftLoading={draftLoading}
                            submitLoading={submitLoading}
                            formik_values_status={formik.values.status}
                        />
                    </>
                );
            case 5:
                return (
                    <>
                        <JobSummary
                            setActiveSteps={setActiveSteps}
                            activeSteps={activeSteps}
                            formik={formik}
                            errors={validationErrors}
                        />
                        <div className={styles.submitButton}>
                            <BubbleButton dark headerDarkButton
                                onClick={() => { setActiveSteps(activeSteps - 1) }}
                                disabled={submitLoading || draftLoading}
                                style={{ padding: '10px 20px' }}>{t('signupHeader.back')}</BubbleButton>
                            
                            {/* Show Save Draft button for new jobs or when editing a draft job */}
                            {(!id || (formik.values.status === 'draft')) && 
                                <BubbleButton
                                    dark
                                    headerDarkButton
                                    onClick={saveAsDraftOnly}
                                    style={{ padding: '10px 20px', marginRight: '10px' }}
                                    disabled={draftLoading || submitLoading}
                                >
                                    {draftLoading ? (
                                        <div className={styles.buttonLoader}>
                                            <div className={styles.spinnerSmall}></div>
                                            <span>{t('forms.login.processing')}</span>
                                        </div>
                                    ) : (
                                        t('job.save_draft', 'Save Draft')
                                    )}
                                </BubbleButton>
                            }
                            
                            <BubbleButton
                                dark
                                headerDarkButton
                                onClick={async () => {
                                    if (submitLoading) return;
                                    
                                    setSubmitLoading(true);
                                    // Validate all steps before final submission
                                    const isStepOneValid = validateStep(1, 2);
                                    const isStepTwoValid = validateStep(2, 3);
                                    const isStepThreeValid = validateStep(3, 4);
                                    const isStepFourValid = validateStep(4, 5);
                                    if (isStepOneValid && isStepTwoValid && isStepThreeValid && isStepFourValid) {
                                        const canPostJob = await checkJobPostingLimits();
                                        if (canPostJob) {
                                            // For draft jobs or new jobs, ensure we set status to active/pending ONLY when fully submitting
                                            if (draftJobId || !id) {
                                                formik.setFieldValue(
                                                    'status', 
                                                    features.instantJobActivation ? 'active' : 'pending'
                                                );
                                            }
                                            
                                            formik.submitForm();
                                            localStorage.setItem('formData', JSON.stringify(defaultValue));
                                            setActiveSteps(1);
                                        } else {
                                            setSubmitLoading(false);
                                        }
                                    } else {
                                        toast.dismiss();
                                        toast.error(t('job.please_fill_all_required_fields'));
                                        setSubmitLoading(false);
                                    }
                                }}
                                style={{ padding: '10px 20px' }}
                                disabled={submitLoading || draftLoading}
                            >
                                {submitLoading ? (
                                    <div className={styles.buttonLoader}>
                                        <div className={styles.spinnerSmall}></div>
                                        <span>{t('forms.login.processing')}</span>
                                    </div>
                                ) : (
                                    formik.values.status === 'draft' ? t('job.publish', 'Publish') : t('job.update', 'Update')
                                )}
                            </BubbleButton>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    // Function to check job posting limits
    const checkJobPostingLimits = async () => {
        try {
            const response = await getJobsList(1, 99999, null, { employer_id: user.id }, user.employer_status || user.jobseeker_status);
            if (features?.monthlyJobsLimit && response?.jobs.filter((e) => e.status == 'active').length >= features.monthlyJobsLimit) {
                setShowLimitModal(true);
                return false;
            }
            return true;
        } catch (error) {
             return true; // Allow posting if check fails
        }
    };

    // Function to save job as draft without navigation
    const saveAsDraftOnly = async () => {
        if (formLoading || draftLoading) return;

        setDraftLoading(true);
        const formData = new FormData();
        formData.append('token', userToken);

        // Basic required fields that shouldn't be empty
        formData.append('title', formik.values.title || '');
        formData.append('city', formik.values.city || '');
        formData.append('country', formik.values.country || '');
        formData.append('category', formik.values.category || '');
        
        // Use the HTML version of description if available, otherwise use the plain text or original description
        const descriptionToSubmit = formik.values.descriptionHtml || formik.values.descriptionPlainText || formik.values.description;
        formData.append('description', typeof descriptionToSubmit === 'string' ? descriptionToSubmit : JSON.stringify(descriptionToSubmit));

        // Boolean fields
        formData.append('specifiedforcountry', formik.values.specifiedForCountry ? true : false);
        formData.append('specifiedforcity', formik.values.specifiedForCity ? true : false);

        // Numeric fields - ensure they're always valid numbers
        formData.append('minworkexp', formik.values.minworkexp ? Number(formik.values.minworkexp) : 0);
        formData.append('maxworkexp', formik.values.maxworkexp ? Number(formik.values.maxworkexp) : 0);

        // String fields
        formData.append('nationalityrequirment', formik.values.nationalityRequirment || 'Yes');
        formData.append('licenserequire', formik.values.licenseRequire || 'Yes');
        formData.append('visarequirment', formik.values.visaRequirment || 'Yes');
        formData.append('salaryrate', formik.values.salaryRate || 'per_hour');
        formData.append('otherbenefit', formik.values.otherBenefit || []);

        const date = new Date();
        date.setDate(date.getDate() + 30);
        formik.setFieldValue('endDate', date.toISOString().split('T')[0]);
   
        formData.append(`education_level`, formik.values.education_level || '');

        // More fields
        formData.append('exlusiveJob', formik.values.exlusiveJob === true ? 'true' : 'false');
        formData.append('job_type', formik.values.job_type || 'full_time');
        formData.append('job_location', formik.values.job_location || 'remote');
        formData.append('currency', formik.values.currency || 'usd');

        // Numeric fields with currency conversions
        const salaryLow = formik.values.salary_low ? Number(formik.values.salary_low) : 0;
        const salaryHigh = formik.values.salary_high ? Number(formik.values.salary_high) : 0;

        formData.append('salary_from_aed', salaryLow * 3.67);
        formData.append('salary_from_sar', salaryLow * 3.75);
        formData.append('salary_from_qar', salaryLow * 3.66);
        formData.append('salary_from_omr', salaryLow * 0.38);
        formData.append('salary_from_kwd', salaryLow * 0.31);
        formData.append('salary_from_bhd', salaryLow * 0.38);
        formData.append('salary_from', salaryLow);
        formData.append('salary_to', salaryHigh);
        formData.append('salary_to_aed', salaryHigh * 3.67);
        formData.append('salary_to_sar', salaryHigh * 3.75);
        formData.append('salary_to_qar', salaryHigh * 3.66);
        formData.append('salary_to_omr', salaryHigh * 0.38);
        formData.append('salary_to_kwd', salaryHigh * 0.31);
        formData.append('salary_to_bhd', salaryHigh * 0.38);

        // Hours fields
        const hoursLow = formik.values.hours_low ? Number(formik.values.hours_low) : 0;
        const hoursHigh = formik.values.hours_high ? Number(formik.values.hours_high) : 0;
        formData.append('hours_from', hoursLow);
        formData.append('hours_to', hoursHigh);

        formData.append('job_status', user.employer_status || 'active');
        
        // Always set status to 'draft' when using save draft button
        formData.append('status', 'draft');
        
        formData.append('nationality', formik.values.nationality || '');

        if (formik.values.languages && formik.values.languages.length > 0) {
            formik.values.languages.forEach((language, index) => {
                if (language) {
                    formData.append(`languages[${index}][languageCode]`, language);
                }
            });
        }
        else {
            formData.append('languages', '');
        }

        if (formik.values.skills && formik.values.skills.length > 0) {
            formik.values.skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('skills', '');
        }

        if (formik.values.role_skills && formik.values.role_skills.length > 0) {
            formik.values.role_skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`role_skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('role_skills', '');
        }

        if (draftJobId) {
            formData.append('job_id', draftJobId);
        }
        else if (id) {
            formData.append('job_id', id);
        }

        try {
            // Dismiss all existing toasts before showing a new one
            toast.dismiss();

            // Show a toast notification for saving as draft
            const updatePromise = updateJob(formData);

            toast.promise(updatePromise, {
                loading: t('forms.login.processing') || 'Processing...',
                success: <b>{t('job.job_saved_as_draft') || 'Job saved as draft!'}</b>,
                error: (err) => <b>{err.error || t('forms.login.error') || 'Error occurred'}</b>,
            });

            const response = await updatePromise;

            // If this is a new job creation (first save), store the job ID
            if (!draftJobId && !id && response && response.job_id) {
                setDraftJobId(response.job_id);
                localStorage.setItem('draftJobId', response.job_id);
            }

            setDraftLoading(false);
            
            // Close the edit section after saving the draft
            setPostJobEnable(false);
            navigate('/employer-portal/jobs');
        } catch (error) {
            if (error?.response?.data?.field) {
                formik.setErrors({
                    [error.response.data.field]: error.response.data.error
                });
            }
            setDraftLoading(false);
        }
    };

    // Function to update a published job without navigation
    const updateJobOnly = async () => {
        if (formLoading || submitLoading) return;

        setSubmitLoading(true);
        const formData = new FormData();
        formData.append('token', userToken);

        // Basic required fields that shouldn't be empty
        formData.append('title', formik.values.title || '');
        formData.append('city', formik.values.city || '');
        formData.append('country', formik.values.country || '');
        formData.append('category', formik.values.category || '');
        
        // Use the HTML version of description if available, otherwise use the plain text or original description
        const descriptionToSubmit = formik.values.descriptionHtml || formik.values.descriptionPlainText || formik.values.description;
        formData.append('description', typeof descriptionToSubmit === 'string' ? descriptionToSubmit : JSON.stringify(descriptionToSubmit));

        // Boolean fields
        formData.append('specifiedforcountry', formik.values.specifiedForCountry ? true : false);
        formData.append('specifiedforcity', formik.values.specifiedForCity ? true : false);

        // Numeric fields - ensure they're always valid numbers
        formData.append('minworkexp', formik.values.minworkexp ? Number(formik.values.minworkexp) : 0);
        formData.append('maxworkexp', formik.values.maxworkexp ? Number(formik.values.maxworkexp) : 0);

        // String fields
        formData.append('nationalityrequirment', formik.values.nationalityRequirment || 'Yes');
        formData.append('licenserequire', formik.values.licenseRequire || 'Yes');
        formData.append('visarequirment', formik.values.visaRequirment || 'Yes');
        formData.append('salaryrate', formik.values.salaryRate || 'per_hour');
        formData.append('otherbenefit', formik.values.otherBenefit || []);

        formData.append('enddate', formik.values.endDate || new Date().toISOString());
        formData.append(`education_level`, formik.values.education_level || '');

        // More fields
        formData.append('exlusiveJob', formik.values.exlusiveJob === true ? 'true' : 'false');
        formData.append('job_type', formik.values.job_type || 'full_time');
        formData.append('job_location', formik.values.job_location || 'remote');
        formData.append('currency', formik.values.currency || 'usd');

        // Numeric fields with currency conversions
        const salaryLow = formik.values.salary_low ? Number(formik.values.salary_low) : 0;
        const salaryHigh = formik.values.salary_high ? Number(formik.values.salary_high) : 0;

        formData.append('salary_from_aed', salaryLow * 3.67);
        formData.append('salary_from_sar', salaryLow * 3.75);
        formData.append('salary_from_qar', salaryLow * 3.66);
        formData.append('salary_from_omr', salaryLow * 0.38);
        formData.append('salary_from_kwd', salaryLow * 0.31);
        formData.append('salary_from_bhd', salaryLow * 0.38);
        formData.append('salary_from', salaryLow);
        formData.append('salary_to', salaryHigh);
        formData.append('salary_to_aed', salaryHigh * 3.67);
        formData.append('salary_to_sar', salaryHigh * 3.75);
        formData.append('salary_to_qar', salaryHigh * 3.66);
        formData.append('salary_to_omr', salaryHigh * 0.38);
        formData.append('salary_to_kwd', salaryHigh * 0.31);
        formData.append('salary_to_bhd', salaryHigh * 0.38);

        // Hours fields
        const hoursLow = formik.values.hours_low ? Number(formik.values.hours_low) : 0;
        const hoursHigh = formik.values.hours_high ? Number(formik.values.hours_high) : 0;
        formData.append('hours_from', hoursLow);
        formData.append('hours_to', hoursHigh);

        formData.append('job_status', user.employer_status || 'active');
        
        // Keep the current status for published jobs
        if (formik.values.status) {
            formData.append('status', formik.values.status);
        }
        
        formData.append('nationality', formik.values.nationality || '');

        if (formik.values.languages && formik.values.languages.length > 0) {
            formik.values.languages.forEach((language, index) => {
                if (language) {
                    formData.append(`languages[${index}][languageCode]`, language);
                }
            });
        }
        else {
            formData.append('languages', '');
        }

        if (formik.values.skills && formik.values.skills.length > 0) {
            formik.values.skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('skills', '');
        }

        if (formik.values.role_skills && formik.values.role_skills.length > 0) {
            formik.values.role_skills.forEach((skill, index) => {
                if (skill) {
                    formData.append(`role_skills[${index}][code]`, skill);
                }
            });
        }
        else {
            formData.append('role_skills', '');
        }

        if (id) {
            formData.append('job_id', id);
        }

        try {
            // Dismiss all existing toasts before showing a new one
            toast.dismiss();

            // Show a toast notification for updating
            const updatePromise = updateJob(formData);

            toast.promise(updatePromise, {
                loading: t('forms.login.processing') || 'Processing...',
                success: <b>{t('job.job_updated') || 'Job updated successfully!'}</b>,
                error: (err) => <b>{err.error || t('forms.login.error') || 'Error occurred'}</b>,
            });

            await updatePromise;
            
            setSubmitLoading(false);
            
            // Close the edit section after updating
            setPostJobEnable(false);
            navigate('/employer-portal/jobs');
        } catch (error) {
            if (error?.response?.data?.field) {
                formik.setErrors({
                    [error.response.data.field]: error.response.data.error
                });
            }
            setSubmitLoading(false);
        }
    };

    return (
        <>
            {/* Success Modal - placed first to ensure it's at the top level */}
            <GlobalModal
                show={showSuccessModal}
                onClose={() => {
                    localStorage.removeItem('draftJobId');
                    setDraftJobId(null);
                    setShowSuccessModal(false);
                    setFormLoading(false);
                    setPostJobEnable(false);
                    navigate('/employer-portal/jobs');
                }}
                title={t('job.success_title', 'Success!')}
                message={
                    submittedJobData.isActive
                    ? t('job.active_job_success_message', 
                        'Your job posting for {{jobTitle}} has been successfully published and is now active. You will start receiving applications shortly.',
                        { jobTitle: submittedJobData.title })
                    : t('job.pending_job_success_message',
                        'Your job posting for {{jobTitle}} has been submitted successfully and is pending approval. You will be notified once it is approved.',
                        { jobTitle: submittedJobData.title })
                }
                primaryButtonText={t('general.UI.view', 'VIEW')}
                secondaryButtonText={t('general.UI.ok', 'OK')}
                primaryButtonAction={() => {
                    localStorage.removeItem('draftJobId');
                    setDraftJobId(null);
                    setShowSuccessModal(false);
                    // Navigate to the job details page
                    navigate(`/jobs/${submittedJobData.id}`);
                }}
                secondaryButtonAction={() => {
                    setShowSuccessModal(false);
                    // Navigate to the jobs list
                    setFormLoading(false);
                    setPostJobEnable(false);
                    navigate('/employer-portal/jobs');
                }}
                customClass={styles.successModal}
            />

            <GlobalModal
                show={showLimitModal}
                onClose={() => setShowLimitModal(false)}
                title={t('upgrade.modal_title') || 'Upgrade Your Plan'}
                message={t('upgrade.monthly_limit_reached') || 'You have reached your monthly job posting limit. Please upgrade your plan to post more jobs.'}
                primaryButtonText={t('upgrade.upgrade_now') || 'Upgrade Now'}
                secondaryButtonText={t('general.UI.close') || 'Close'}
                navigateTo="/subscriptions"
                secondaryButtonAction={() => setShowLimitModal(false)}
            />

            <div className={styles.formContainer}>
                <form onSubmit={formik.handleSubmit}>
                    {renderFormStep(activeSteps)}
                </form>
            </div>
        </>
    );
});

export default EditJob;